import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  getGallery,
  createGalleryImage,
  updateGalleryImage,
  deleteGalleryImage,
} from '../services/api';
import './AdminGallery.css';

const EMPTY_FORM = {
  title: '',
  category: '',
  image_url: '',
  description: '',
};

// Well-known categories derived from seed — admin can type freely too
const SUGGESTED_CATEGORIES = ['Culte', 'Sacrements', 'Jeunesse', 'Événements', 'Enfants', 'Mission', 'Prière', 'Ministères'];

export default function AdminGallery() {
  const [images, setImages] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const formRef = useRef(null);

  useEffect(() => { load(); }, []);

  function load() {
    getGallery().then(setImages).catch(() => setError('Impossible de charger la galerie.'));
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  }

  function startEdit(img) {
    setEditingId(img.id);
    setForm({
      title: img.title || '',
      category: img.category || '',
      image_url: img.image_url || '',
      description: img.description || '',
    });
    setError('');
    setSuccess('');
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function cancelEdit() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.title.trim()) return setError('Le titre est requis.');
    if (!form.image_url.trim()) return setError("L'URL de l'image est requise.");
    setSaving(true);
    setError('');
    try {
      if (editingId) {
        await updateGalleryImage(editingId, form);
        setSuccess('Image mise à jour.');
      } else {
        await createGalleryImage(form);
        setSuccess('Image ajoutée.');
      }
      setEditingId(null);
      setForm(EMPTY_FORM);
      load();
    } catch {
      setError('Une erreur est survenue.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(img) {
    if (!window.confirm(`Supprimer « ${img.title} » ?`)) return;
    try {
      await deleteGalleryImage(img.id);
      setSuccess('Image supprimée.');
      load();
    } catch {
      setError('Erreur lors de la suppression.');
    }
  }

  return (
    <div className="ag-page">
      {/* Header */}
      <div className="ag-header">
        <div className="container">
          <div className="ag-header__inner">
            <div>
              <Link to="/gestion-ecclesiale" className="ag-breadcrumb">← Tableau de bord</Link>
              <h1 className="ag-title">Gestion de la Galerie</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="container ag-body">

        {/* ── Form ── */}
        <section className="ag-form-section" ref={formRef}>
          <h2 className="ag-section-title">
            {editingId ? "Modifier l'image" : 'Ajouter une image'}
          </h2>

          {error && <div className="ag-alert ag-alert--error">{error}</div>}
          {success && <div className="ag-alert ag-alert--success">{success}</div>}

          <form className="ag-form" onSubmit={handleSubmit} noValidate>
            <div className="ag-row">
              <div className="ag-field ag-field--grow">
                <label>Titre *</label>
                <input name="title" value={form.title} onChange={handleChange} placeholder="ex: Culte du Dimanche" />
              </div>
              <div className="ag-field">
                <label>Catégorie</label>
                <input
                  name="category"
                  value={form.category}
                  onChange={handleChange}
                  list="category-suggestions"
                  placeholder="ex: Culte"
                />
                <datalist id="category-suggestions">
                  {SUGGESTED_CATEGORIES.map((c) => <option key={c} value={c} />)}
                </datalist>
              </div>
            </div>

            {/* Image URL + preview */}
            <div className="ag-row ag-row--align-start">
              <div className="ag-field ag-field--grow">
                <label>URL de l'image *</label>
                <input name="image_url" value={form.image_url} onChange={handleChange} placeholder="https://..." />
              </div>
              {form.image_url && (
                <div className="ag-img-preview">
                  <img src={form.image_url} alt="Aperçu" />
                </div>
              )}
            </div>

            <div className="ag-field">
              <label>Description</label>
              <textarea name="description" value={form.description} onChange={handleChange} rows={2} placeholder="Légende ou description de la photo..." />
            </div>

            <div className="ag-form-actions">
              <button type="submit" className="ag-btn ag-btn--primary" disabled={saving}>
                {saving ? 'Enregistrement...' : editingId ? 'Mettre à jour' : 'Ajouter'}
              </button>
              {editingId && (
                <button type="button" className="ag-btn ag-btn--ghost" onClick={cancelEdit}>
                  Annuler
                </button>
              )}
            </div>
          </form>
        </section>

        {/* ── Grid ── */}
        <section className="ag-list-section">
          <h2 className="ag-section-title">Images ({images.length})</h2>
          {images.length === 0 ? (
            <p className="ag-empty">Aucune image dans la galerie.</p>
          ) : (
            <div className="ag-grid">
              {images.map((img) => (
                <div key={img.id} className={`ag-card${editingId === img.id ? ' ag-card--editing' : ''}`}>
                  <div className="ag-card__img-wrap">
                    <img src={img.image_url} alt={img.title} className="ag-card__img" />
                    {img.category && <span className="ag-card__cat">{img.category}</span>}
                  </div>
                  <div className="ag-card__body">
                    <p className="ag-card__title">{img.title}</p>
                    {img.description && <p className="ag-card__desc">{img.description}</p>}
                    <div className="ag-card__actions">
                      <button className="ag-action-btn ag-action-btn--edit" onClick={() => startEdit(img)}>Modifier</button>
                      <button className="ag-action-btn ag-action-btn--delete" onClick={() => handleDelete(img)}>Supprimer</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
